import { ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle, CreditCard, FileText } from 'lucide-react';
import { BookingDetails, ExpandedSections } from '../../types/booking-details.types';
import { formatBookingAmount } from '../../utils/booking-details.utils';
import { PAYMENT_CONFIG } from '../../constants/booking-details.constants';
import { ExpandableSection } from './ExpandableSection';
import { cn } from '../../lib/utils';

export function FinancesSection({
  booking,
  expandedSections,
  toggleSection,
}: {
  booking: BookingDetails;
  expandedSections: ExpandedSections;
  toggleSection: (section: keyof ExpandedSections) => void;
}) {
  const b = booking;

  return (
    <ExpandableSection title="Finances & Facturation" icon={CreditCard} isExpanded={true} onToggle={() => {}}>
      <div className="bg-[#1e293b] rounded-xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-6 -mt-6 opacity-10">
          <CreditCard className="w-28 h-28" />
        </div>
        <div className="relative z-10 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm mb-1">Montant Total</p>
              <p className="text-3xl font-bold tracking-tight">{formatBookingAmount(b.financials.amount)}</p>
            </div>
            <div className="text-right space-y-2">
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold',
                  PAYMENT_CONFIG[b.financials.paymentStatus]?.bg,
                  PAYMENT_CONFIG[b.financials.paymentStatus]?.text,
                )}
              >
                {b.financials.paymentStatus === 'PAID' && <CheckCircle2 className="w-3 h-3" />}
                {b.financials.paymentStatus === 'PENDING' && <Clock className="w-3 h-3" />}
                {b.financials.paymentStatus === 'FAILED' && <AlertCircle className="w-3 h-3" />}
                {PAYMENT_CONFIG[b.financials.paymentStatus]?.label}
              </span>
              {b.financials.paymentMethod && (
                <p className="text-xs text-white/50">Via {b.financials.paymentMethod}</p>
              )}
            </div>
          </div>

          {/* Références */}
          <div className="flex items-center justify-between text-sm">
            <div className="space-y-1">
              {b.financials.quoteNumber && <p className="text-white/70">Devis: {b.financials.quoteNumber}</p>}
              {b.financials.invoiceNumber && <p className="text-white/70">Facture: {b.financials.invoiceNumber}</p>}
            </div>
            {b.financials.confirmationCode && (
              <div className="text-right">
                <p className="text-white/60 text-xs">Code confirmation</p>
                <p className="text-white font-mono">{b.financials.confirmationCode}</p>
              </div>
            )}
          </div>

          {/* Décomposition expandable */}
          {b.financials.breakdown && b.financials.breakdown.length > 0 && (
            <div>
              <button
                onClick={() => toggleSection('breakdown')}
                className="flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white transition-colors"
              >
                <FileText className="w-4 h-4" />
                Décomposition détaillée
                {expandedSections.breakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSections.breakdown && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-2.5">
                  {b.financials.breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between text-sm gap-3">
                      <div className="flex flex-col min-w-0">
                        <span className="text-white/80 leading-snug">{item.label}</span>
                        {/* Détail PU × quantité si disponibles */}
                        {item.unitPrice !== undefined && item.quantity !== undefined && item.quantity > 1 && (
                          <span className="text-white/40 text-xs mt-0.5">
                            {formatBookingAmount(item.unitPrice)} × {item.quantity}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold text-white whitespace-nowrap">{formatBookingAmount(item.amount)}</span>
                    </div>
                  ))}
                  <div className="pt-2.5 mt-2.5 border-t border-white/10 flex items-center justify-between">
                    <span className="font-bold text-white">Total</span>
                    <span className="font-bold text-white text-lg">{formatBookingAmount(b.financials.amount)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ExpandableSection>
  );
}
